/**
 * Exibe número no estilo WhatsApp: bandeira + formato nacional.
 * Ex.: 🇧🇷 (35) 9949-8190 | 🇨🇦 (343) 922-2049
 */

/** Área NANP (+1) no Canadá (343 = QC, etc.) */
const CA_AREA_CODES = new Set([
  "204", "226", "236", "249", "250", "263", "289", "306", "343", "354", "365",
  "367", "368", "403", "416", "418", "428", "431", "437", "438", "450", "506",
  "514", "519", "548", "579", "581", "584", "587", "604", "613", "639", "647",
  "672", "683", "705", "709", "742", "753", "778", "780", "782", "807", "819",
  "825", "867", "873", "879", "902", "905", "942"
]);

function digitsOnly(value) {
  return String(value || "")
    .replace("@c.us", "")
    .replace("@s.whatsapp.net", "")
    .replace(/@lid/gi, "")
    .replace(/\D/g, "");
}

/** Número no cadastro é só o identificador LID (não é telefone discável). */
function isLidOnlyNumber(raw, d, lid) {
  if (/@lid/i.test(raw)) return true;

  const lidDigits = lid ? digitsOnly(lid) : "";
  if (lidDigits && d === lidDigits) return true;

  // LID WhatsApp: 14–18 dígitos, sem estrutura E.164 (evita +13 9947…)
  if (d.length >= 14) {
    if (d.startsWith("55") && (d.length === 12 || d.length === 13)) return false;
    if (d.startsWith("1") && d.length === 11) return false;
    if (formatBrazil(d) || formatNanp(d) || formatGenericInternational(d)) {
      return false;
    }
    return true;
  }

  return false;
}

function formatBrazil(d) {
  if (d.length === 12) {
    const m = d.match(/^55(\d{2})(\d{4})(\d{4})$/);
    if (m) return `🇧🇷 (${m[1]}) ${m[2]}-${m[3]}`;
  }
  if (d.length === 13) {
    const m = d.match(/^55(\d{2})(\d{5})(\d{4})$/);
    if (m) return `🇧🇷 (${m[1]}) ${m[2]}-${m[3]}`;
  }
  return null;
}

function formatNanp(d) {
  if (!d.startsWith("1") || d.length !== 11) return null;
  const area = d.slice(1, 4);
  const flag = CA_AREA_CODES.has(area) ? "🇨🇦" : "🇺🇸";
  return `${flag} (${area}) ${d.slice(4, 7)}-${d.slice(7)}`;
}

/** [prefixo discagem, bandeira, formatador do número nacional] */
const INTERNATIONAL = [
  {
    cc: "351",
    flag: "🇵🇹",
    min: 12,
    fmt: r => {
      const m = r.match(/^(\d{2,3})(\d{3})(\d{3,4})$/);
      return m ? `🇵🇹 ${m[1]} ${m[2]} ${m[3]}` : null;
    }
  },
  {
    cc: "54",
    flag: "🇦🇷",
    min: 12,
    fmt: r => {
      const m = r.match(/^(\d{2,4})(\d{6,8})$/);
      return m ? `🇦🇷 (${m[1]}) ${m[2].slice(0, 4)}-${m[2].slice(4)}` : null;
    }
  },
  {
    cc: "52",
    flag: "🇲🇽",
    min: 12,
    fmt: r => {
      const m = r.match(/^(\d{2,3})(\d{3})(\d{4})$/);
      return m ? `🇲🇽 (${m[1]}) ${m[2]} ${m[3]}` : null;
    }
  },
  {
    cc: "44",
    flag: "🇬🇧",
    min: 12,
    fmt: r => {
      if (r.length === 10) {
        return `🇬🇧 ${r.slice(0, 4)} ${r.slice(4, 7)} ${r.slice(7)}`;
      }
      return null;
    }
  },
  {
    cc: "49",
    flag: "🇩🇪",
    min: 12,
    fmt: r => {
      const m = r.match(/^(\d{3,4})(\d{6,8})$/);
      return m ? `🇩🇪 ${m[1]} ${m[2].slice(0, 3)} ${m[2].slice(3)}` : null;
    }
  },
  {
    cc: "33",
    flag: "🇫🇷",
    min: 11,
    fmt: r => {
      const m = r.match(/^(\d)(\d{2})(\d{2})(\d{2})(\d{2})$/);
      return m ? `🇫🇷 ${m[1]} ${m[2]} ${m[3]} ${m[4]} ${m[5]}` : null;
    }
  },
  {
    cc: "39",
    flag: "🇮🇹",
    min: 11,
    fmt: r => {
      const m = r.match(/^(\d{2,3})(\d{6,7})$/);
      return m ? `🇮🇹 ${m[1]} ${m[2].slice(0, 3)} ${m[2].slice(3)}` : null;
    }
  },
  {
    cc: "34",
    flag: "🇪🇸",
    min: 11,
    fmt: r => {
      const m = r.match(/^(\d{3})(\d{3})(\d{3})$/);
      return m ? `🇪🇸 ${m[1]} ${m[2]} ${m[3]}` : null;
    }
  },
  {
    cc: "61",
    flag: "🇦🇺",
    min: 11,
    fmt: r => {
      const m = r.match(/^(\d)(\d{4})(\d{4})$/);
      return m ? `🇦🇺 ${m[1]} ${m[2]} ${m[3]}` : null;
    }
  },
  {
    cc: "91",
    flag: "🇮🇳",
    min: 12,
    fmt: r => {
      const m = r.match(/^(\d{5})(\d{5})$/);
      return m ? `🇮🇳 ${m[1]} ${m[2]}` : null;
    }
  }
];

function formatGenericInternational(d) {
  const sorted = [...INTERNATIONAL].sort((a, b) => b.cc.length - a.cc.length);
  for (const { cc, flag, min, fmt } of sorted) {
    if (!d.startsWith(cc) || d.length < cc.length + (min - cc.length)) continue;
    const rest = d.slice(cc.length);
    const formatted = fmt(rest);
    if (formatted) return formatted;
    return `${flag} +${cc} ${rest}`;
  }
  return null;
}

/** ID numérico de grupo WhatsApp (evita confundir com LID de contato 1:1). */
function formatGroupId(raw, d) {
  if (raw.includes("@g.us")) {
    return raw;
  }
  return d ? `${d}@g.us` : raw;
}

/**
 * @param {string|number} phone
 * @param {{ lid?: string, isGroup?: boolean, channel?: string, username?: string }} [options]
 * @returns {string}
 */
export function formatWhatsAppPhone(phone, options = {}) {
  if (phone === undefined || phone === null || phone === "") {
    return "-";
  }

  const raw = String(phone).trim();
  const channel = String(options.channel || "").toLowerCase();

  if (channel === "instagram") {
    if (options.username) {
      return `@${String(options.username).replace(/^@/, "")}`;
    }
    return `Instagram ID: ${raw}`;
  }

  if (channel === "facebook") {
    return `Facebook ID: ${raw}`;
  }

  if (raw.includes("@g.us")) {
    return raw;
  }

  const d = digitsOnly(raw);
  if (!d) {
    return "-";
  }

  if (options.isGroup) {
    return formatGroupId(raw, d);
  }

  // Grupo salvo só com dígitos (ex.: 120363038460304929) — não é LID nem telefone
  if (
    d.length >= 15 &&
    d.length <= 22 &&
    !formatBrazil(d) &&
    !formatNanp(d) &&
    !formatGenericInternational(d)
  ) {
    return formatGroupId(raw, d);
  }

  if (isLidOnlyNumber(raw, d, options.lid)) {
    return "—";
  }

  const br = formatBrazil(d);
  if (br) return br;

  const nanp = formatNanp(d);
  if (nanp) return nanp;

  const intl = formatGenericInternational(d);
  if (intl) return intl;

  if (d.length >= 10 && d.length <= 13) {
    if (d.startsWith("1") && d.length === 11) return raw;
    const cc = d.slice(0, 2);
    if (["55", "44", "49", "33", "39", "34", "52", "54", "61", "91", "351"].includes(cc)) {
      return `+${cc} ${d.slice(2)}`;
    }
  }

  return raw;
}

export default formatWhatsAppPhone;
